import React, { useContext, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { LIST_APP_RUNS } from '../../../../graphql/query';
import { AppContext } from '../../../../contexts/AppContext';
import { DELETE_APP_RUN, DUPLICATE_APP_RUN, SET_APP_RUN_NAME } from '../../../../graphql/mutation';
import {
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
} from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlineRoundedIcon from '@mui/icons-material/InfoOutlineRounded';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEditorStore } from '../../../../hooks/EditorState';
import { DeleteButton } from './DeleteButton';
import { NameChangeV2 } from '../../Editor/NameChange';
import { FlowNodeName, FlowNodeNameWithVersion } from '../FlowNodeName';
import { AppRunInfo } from '../../../../../generated/gql/graphql';

const PAGE_SIZE = 10; // Number of items per page

// Add fixed-length date formatter
function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}

export function RunHistoryPanel(props: {
  onResume: (clientId: string) => void;
}): React.ReactElement {
  const { setError, setSuccessMessage } = useContext(AppContext);
  const appId = useEditorStore(state => state.app.id);
  const [runs, setRuns] = useState<AppRunInfo[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [processingRunIds, setProcessingRunIds] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // New state for dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState<AppRunInfo | null>(null);

  const { data, loading, fetchMore, refetch } = useQuery(LIST_APP_RUNS, {
    variables: {
      flowId: appId as string,
      limit: PAGE_SIZE,
    },
    skip: !appId,
    onError: setError,
  });

  // mutation to delete a run
  const [deleteAppRun, { loading: isDeletingRun }] = useMutation(DELETE_APP_RUN, {
    onError: setError,
  });

  // mutation to duplicate a run
  const [duplicateAppRun, { loading: isDuplicatingRun }] = useMutation(DUPLICATE_APP_RUN, {
    onError: setError,
  });

  // mutation to set a run name
  const [setAppRunName, { loading: isSettingName }] = useMutation(SET_APP_RUN_NAME, {
    onError: setError,
  });

  useEffect(() => {
    if (data?.appRuns?.results) {
      setRuns(data.appRuns.results);
    }
  }, [data]);

  const loadMoreRuns = async () => {
    if (isLoadingMore || !data?.appRuns?.pageInfo?.hasNext) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          flowId: appId,
          skip: runs.length, // Skip already loaded runs
          limit: PAGE_SIZE,
        },
      });

      if (result.data.appRuns?.results) {
        setRuns(prevRuns => [...prevRuns, ...result.data.appRuns.results]);
      }
    } catch (err) {
      setError(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleDelete = async (clientId: string) => {
    setProcessingRunIds(prev => [...prev, clientId]);
    try {
      await deleteAppRun({ variables: { clientId } });
      setRuns(prev => prev.filter(r => r.clientId !== clientId));
      setSuccessMessage(`Run ${clientId} deleted.`);
    } finally {
      setProcessingRunIds(prev => prev.filter(id => id !== clientId));
    }
  };

  const handleDuplicate = async (clientId: string) => {
    setProcessingRunIds(prev => [...prev, clientId]);
    try {
      await duplicateAppRun({ variables: { clientId } });
      await refetch(); // Refresh the run list after duplication
      setSuccessMessage(`Run ${clientId} duplicated successfully.`);
    } finally {
      setProcessingRunIds(prev => prev.filter(id => id !== clientId));
    }
  };

  const handleNameChange = async (clientId: string, name: string) => {
    if (!name.trim()) return;

    setProcessingRunIds(prev => [...prev, clientId]);
    try {
      await setAppRunName({ variables: { clientId, name } });
      // Update the local state with the new name
      setRuns(prev => prev.map(r => r.clientId === clientId ? { ...r, name } : r));
      setSuccessMessage(`Run name updated successfully.`);
    } finally {
      setProcessingRunIds(prev => prev.filter(id => id !== clientId));
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handler to open the details dialog
  const handleOpenDetails = (run: any) => {
    setSelectedRun(run);
    setDialogOpen(true);
  };

  // Handler to close the details dialog
  const handleCloseDetails = () => {
    setDialogOpen(false);
    setSelectedRun(null);
  };

  if (!appId) {
    return <Typography>Save the app to view run history.</Typography>;
  }

  if (loading && !runs.length) {
    return <LinearProgress />;
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || isLoadingMore || isRefreshing}
          size="small"
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {runs.map((run) => {
              const isProcessing = processingRunIds.includes(run.clientId);
              const displayText = run.name || `Run updated at ${formatDate(run.updated)}`;

              return (
                <TableRow
                  key={run.clientId}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '& .MuiTableCell-root': { display: 'flex', alignItems: 'center' },
                    display: 'flex',
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ flexGrow: 1 }}>
                    <div style={{ width: '100%' }}>
                      <NameChangeV2
                        value={run.name || ''}
                        onChange={(newName) => handleNameChange(run.clientId, newName)}
                        display={
                          <Typography color={run.hasError ? "error" : "inherit"}>
                            {displayText}
                            {run.hasError ? " (failed)" : ""}
                          </Typography>
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell sx={{ gap: 1 }}>
                    <IconButton
                      color='primary'
                      onClick={() => props.onResume(run.clientId)}
                      disabled={isProcessing || isDeletingRun || isDuplicatingRun || isSettingName}
                    >
                      <PlayCircleOutlineRoundedIcon color='secondary' fontSize='small' />
                    </IconButton>
                    <IconButton
                      color='primary'
                      onClick={() => handleOpenDetails(run)}
                      size="small"
                    >
                      <InfoOutlineRoundedIcon fontSize='small' />
                    </IconButton>
                    {isProcessing ? (
                      <CircularProgress size={24} thickness={4} color="primary" />
                    ) : (
                      <IconButton
                        color='primary'
                        onClick={() => handleDuplicate(run.clientId)}
                        disabled={isProcessing || isDeletingRun || isDuplicatingRun || isSettingName}
                        size="small"
                      >
                        <ContentCopyIcon fontSize='small' />
                      </IconButton>
                    )}
                    <DeleteButton
                      onDelete={() => handleDelete(run.clientId)}
                      disabled={isProcessing || isDeletingRun || isDuplicatingRun || isSettingName}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {data?.appRuns?.pageInfo?.hasNext && (
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '& .MuiTableCell-root': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                  display: 'flex',
                }}
              >
                <TableCell colSpan={2} align="center" sx={{ justifyContent: 'center' }}>
                  <Button
                    onClick={loadMoreRuns}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load More"}
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {runs.length === 0 && (
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '& .MuiTableCell-root': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                  display: 'flex',
                }}
              >
                <TableCell colSpan={2} align="center" sx={{ justifyContent: 'center' }}>
                  <Typography>No run history available.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoadingMore && <LinearProgress />}

      {/* Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="md"
      >
        {selectedRun && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedRun.name ? `Run Details: ${selectedRun.name}` : 'Run Details'}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleCloseDetails} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Run ID</Typography>
                  <Typography variant="body1">{selectedRun.clientId}</Typography>
                </Box>
                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1">{formatDate(selectedRun.updated)}</Typography>
                </Box>
                <Divider />

                {selectedRun.currentNodeId && <>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="text.secondary">Current Node</Typography>
                    <Box sx={{ width: 'fit-content', display: 'inline-block' }}>
                      <FlowNodeNameWithVersion nodeId={selectedRun.currentNodeId} nodeVersion={selectedRun.currentNodeVersion} />
                    </Box>
                  </Stack>
                  <Divider />
                </>}

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Typography variant="body1" color={selectedRun.hasError ? "error.main" : "success.main"}>
                    {selectedRun.hasError ? "Failed" : "Success"}
                  </Typography>
                </Box>

                {selectedRun.hasError && selectedRun.errorDetails && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Error Details</Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: 'error.light',
                          color: 'error.contrastText',
                          p: 2,
                          borderRadius: 1,
                          maxHeight: '200px',
                          overflow: 'auto'
                        }}
                      >
                        <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {selectedRun.errorDetails}
                        </Typography>
                      </Paper>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  props.onResume(selectedRun.clientId);
                  handleCloseDetails();
                }}
              >
                Resume Run
              </Button>
            </DialogActions>
          </>
        )
        }
      </Dialog >
    </Stack >
  );
}

export default RunHistoryPanel;
